import axios from 'axios';
import { amount, shortDay } from './licences';

/**
 * The contractor contracts register (migration 120) from the browser: the
 * calls, and the words every contract screen uses - so the list, a
 * contract's page, the Contract tab on a contractor's profile and the
 * contractor's own card in the portal say "Notice by 30 Jul 2027" and "8 of
 * 10 hours - 2 left" the same way.
 *
 * Administrators only for the register (routes/contracts.js); the portal
 * card reads the contractor's own terms through the portal's route, which
 * leaves out the notes and the documents not shared.
 */
const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export const contractsApi = {
  list: (params) => axios.get(`${API}/contracts`, { params }).then((r) => r.data),
  summary: () => axios.get(`${API}/contracts/summary`).then((r) => r.data),
  exportRows: (params) => axios.get(`${API}/contracts/export`, { params }).then((r) => r.data),
  options: () => axios.get(`${API}/contracts/options`).then((r) => r.data),
  forContractor: (id) => axios.get(`${API}/contracts/contractor/${id}`).then((r) => r.data),
  get: (id) => axios.get(`${API}/contracts/${id}`).then((r) => r.data),
  create: (body) => axios.post(`${API}/contracts`, body).then((r) => r.data),
  update: (id, body) => axios.patch(`${API}/contracts/${id}`, body).then((r) => r.data),
  status: (id, status, reason) => axios.post(`${API}/contracts/${id}/status`, { status, reason }).then((r) => r.data),
  remove: (id) => axios.delete(`${API}/contracts/${id}`).then((r) => r.data),
  addRate: (id, body) => axios.post(`${API}/contracts/${id}/rates`, body).then((r) => r.data),
  removeRate: (id, rateId) => axios.delete(`${API}/contracts/${id}/rates/${rateId}`).then((r) => r.data),
  upload: (id, form) => axios.post(`${API}/contracts/${id}/documents`, form).then((r) => r.data),
  share: (id, docId, shareable) => axios.patch(`${API}/contracts/${id}/documents/${docId}`, { shareable }).then((r) => r.data),
  removeDocument: (id, docId) => axios.delete(`${API}/contracts/${id}/documents/${docId}`).then((r) => r.data),
  documentPath: (id, docId) => `/contracts/${id}/documents/${docId}`,
  // The contractor's own (the portal): their token, or an administrator's in View as.
  mine: (contractorId, auth) => axios.get(`${API}/contractors/${contractorId}/contracts`, auth).then((r) => r.data),
  myDocumentPath: (contractorId, id, docId) => `/contractors/${contractorId}/contracts/${id}/documents/${docId}`
};

/** The sentence a refusal carries, or `fallback`. */
export const errorOf = (e, fallback = 'That did not work.') => e?.response?.data?.error || fallback;

export const STATUS_LABEL = { draft: 'Draft', active: 'Active', ended: 'Ended', cancelled: 'Cancelled' };
export const statusTone = (s) => ({ draft: 'info', active: 'success', ended: 'idle', cancelled: 'idle' }[s] || 'idle');
export const DOC_LABEL = { contract: 'Signed contract', amendment: 'Amendment', rates: 'Rate schedule', other: 'Other' };

/** "€60.00 an hour". */
export const rateWords = (n, currency = 'EUR') => (n === null || n === undefined ? null : `${amount(n, currency)} an hour`);

/**
 * THE "NO RATE FOR THIS DATE" WARNING (Gordon, 3 Nov): a contract covers the
 * day worked but its rate card has nothing in force then, so the work is
 * priced at the contractor's own rate. The server's rate lookup says so
 * (rate_source 'contract_no_rate', and the day it looked at); this only
 * words it: "No contract rate for 20 Feb 2026 — using FixIt’s own rate
 * (€55.00/h)". `who` is the contractor's company, else their name. Null
 * when the rate is not a fallback of that kind.
 */
export function noRateWords({ day, rate, who } = {}) {
  const on = day ? shortDay(day) : 'this date';
  return `No contract rate for ${on} — using ${who ? `${who}’s` : 'the contractor’s'} own rate (${amount(rate)}/h)`;
}

/** The warning for a queue row or an approval's detail, or null. */
export const rowNoRate = (x, who) => (x && x.rate_source === 'contract_no_rate'
  ? noRateWords({ day: x.rate_day, rate: x.hourly_rate, who }) : null);

const KIND_VERB = { end: 'Ends', renewal: 'Renews', notice: 'Notice due' };

/**
 * The contract's next date as a chip: { text, tone } - "Notice due in 12
 * days", "Ends today", "Renews 1 May 2027", "End date passed 3 days ago".
 * Null when there is none (or it is not active).
 */
export function nextWords(k) {
  if (!k || k.status !== 'active' || !k.next_on) return null;
  const d = Number(k.days_left);
  const verb = KIND_VERB[k.next_kind] || 'Due';
  if (d < 0) return { text: `${k.next_kind === 'end' ? 'End date' : 'Renewal date'} passed ${-d} day${d === -1 ? '' : 's'} ago`, tone: 'critical' };
  if (d === 0) return { text: `${verb} today`, tone: 'critical' };
  if (d <= 7) return { text: `${verb} in ${d} day${d === 1 ? '' : 's'}`, tone: 'critical' };
  if (d <= 30) return { text: `${verb} in ${d} days`, tone: 'warning' };
  if (d <= 90) return { text: `${verb} in ${d} days`, tone: 'info' };
  return { text: `${verb} ${shortDay(k.next_on)}`, tone: 'idle' };
}

const hrs = (n) => {
  const v = Math.round((Number(n) || 0) * 100) / 100;
  return `${v} hour${v === 1 ? '' : 's'}`;
};

/**
 * A cap as a line and a tone: "8 of 10 hours - 2 left", "€570.00 of €500.00
 * - over by €70.00". `cap` is the contract's cap.hours or cap.amount.
 */
export function capWords(cap, kind, currency = 'EUR') {
  if (!cap) return null;
  const show = (n) => (kind === 'hours' ? hrs(n) : amount(n, currency));
  const of = kind === 'hours' ? `${Math.round(cap.used * 100) / 100} of ${hrs(cap.cap)}` : `${amount(cap.used, currency)} of ${amount(cap.cap, currency)}`;
  if (cap.over_by > 0) return { text: `${of} - over by ${show(cap.over_by)}`, tone: 'critical', percent: cap.percent };
  if (cap.stage === 100) return { text: `${of} - all used`, tone: 'critical', percent: cap.percent };
  if (cap.stage === 80) return { text: `${of} - ${show(cap.left)} left`, tone: 'warning', percent: cap.percent };
  return { text: `${of} - ${show(cap.left)} left`, tone: 'success', percent: cap.percent };
}
